/**
 * Cmarkdowntreemap Component
 * Renders a hierarchical treemap from mermaid treemap syntax using React Flow.
 * Sections are titled container rects; leaves are value-proportional cells laid
 * out by a squarified tiling. Supports hover-dim, drill-down + breadcrumb, and
 * keyboard navigation.
 */

'use client';

import type { CSSProperties, ReactElement } from 'react';
import { useRef, useState } from 'react';
import '@xyflow/react/dist/style.css';
import {
  Cmarkdownvisual,
  CmarkdownvisualError,
  CmarkdownvisualErrorReason,
  CmarkdownvisualType,
} from '../Cmarkdownvisual';
import { TreemapControls } from './TreemapControls';
import { TreemapFlowCanvas } from './TreemapFlowCanvas';
import { TREEMAP_METRIC_UNIT } from './treemap-constants';
import { formatTreemapValue } from './treemap-format';
import { TreemapHoverContext } from './treemap-hover-context';
import { hasRenderableTreemapLayout } from './treemap-view-model';
import { useTreemapInteractions } from './useTreemapInteractions';
import { useTreemapRenderModel } from './useTreemapRenderModel';

const CONTENT_MIN_HEIGHT = 380;

export interface CmarkdowntreemapProps {
  content: string;
  className?: string;
  style?: CSSProperties;
}

export function Cmarkdowntreemap({
  content,
  className,
  style,
}: CmarkdowntreemapProps): ReactElement {
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const canvasWrapperRef = useRef<HTMLDivElement | null>(null);
  const model = useTreemapRenderModel({ content, hoveredNodeId });
  const interactions = useTreemapInteractions({
    parsed: model.parsed,
    layout: model.layout,
    parentMap: model.parentMap,
    rectById: model.rectById,
    hoveredNodeId,
    setHoveredNodeId,
    canvasWrapperRef,
  });
  const layout = model.layout;

  if (!model.parsed) {
    return (
      <CmarkdownvisualError
        dataElScope="Cmarkdowntreemap/Visual"
        type={CmarkdownvisualType.Treemap}
        reason={CmarkdownvisualErrorReason.Unparseable}
      />
    );
  }

  if (!hasRenderableTreemapLayout(layout)) {
    return (
      <CmarkdownvisualError
        dataElScope="Cmarkdowntreemap/Visual"
        type={CmarkdownvisualType.Treemap}
        reason={CmarkdownvisualErrorReason.Empty}
      />
    );
  }

  const controls = (
    <TreemapControls
      isMinimapVisible={interactions.isMinimapVisible}
      isReady={interactions.isReady}
      zoomLevel={interactions.zoomLevel}
      onZoomIn={interactions.handleZoomIn}
      onZoomOut={interactions.handleZoomOut}
      onZoomSliderChange={interactions.handleZoomSliderChange}
      onResetView={interactions.resetView}
      onToggleMinimap={interactions.handleToggleMinimap}
    />
  );

  return (
    <Cmarkdownvisual
      dataElScope="Cmarkdowntreemap/Visual"
      type={CmarkdownvisualType.Treemap}
      controls={controls}
      modalControls={controls}
      contentStyle={{ minHeight: CONTENT_MIN_HEIGHT, flex: 1 }}
      tabIndex={0}
      onKeyDown={interactions.handleKeyDown}
      style={{ outline: 'none', ...style }}
      className={className}
      compactPreview={model.compactPreview}
      metric={{ value: formatTreemapValue(model.totalValue), unit: TREEMAP_METRIC_UNIT }}
    >
      <TreemapHoverContext.Provider value={model.interactionState}>
        <TreemapFlowCanvas
          layout={layout}
          parsed={model.parsed}
          drillPathIds={interactions.drillPathIds}
          isMinimapVisible={interactions.isMinimapVisible}
          canvasWrapperRef={canvasWrapperRef}
          onDrillToDepth={interactions.drillToDepth}
          onReactFlowInit={interactions.handleReactFlowInit}
          onReactFlowMove={interactions.handleReactFlowMove}
          onNodeClick={interactions.onNodeClick}
          onNodeMouseEnter={interactions.onNodeMouseEnter}
          onNodeMouseLeave={interactions.onNodeMouseLeave}
          onPaneClick={interactions.handlePaneClick}
        />
      </TreemapHoverContext.Provider>
    </Cmarkdownvisual>
  );
}
